import React from 'react';
import { Alert } from 'react-bootstrap';
import { Path, useForm } from 'react-hook-form';
import KaModal from '../Common/KaModal';
import KaAlert from '../Common/KaAlert';
import SpinnerButton from '../Common/SpinnerButton';
import useDisplayMessage from '../Common/useDisplayMessage';
import { RowFormText } from '../Common/Forms/FormText';
import { BusinessDto, SiteDto } from '../../requests/employee/useGetBusinesses';
import useCreateSite, { UpsertSiteDto } from '../../requests/employee/useCreateSite';
import useUpdateSite from '../../requests/employee/useUpdateSite';

type IProps = {
  onSuccess: (site: UpsertSiteDto) => void;
  onHide: () => void;
} & (
    | { business: BusinessDto; site?: undefined }
    | { business?: undefined; site: SiteDto }
  );

export const CreateEditSite = (props: IProps) => {
  const isEdit = props.site !== undefined;
  const atSiteAllowance = !isEdit && props.business!.sites.length >= props.business!.siteAllowance;
  const displayMessage = useDisplayMessage();

  const { register, handleSubmit, getValues, setValue, formState: { errors } } = useForm<UpsertSiteDto>({
    defaultValues: isEdit
      ? {
        customerName: props.site!.customerName,
        internalName: props.site!.internalName,
        internalSiteId: props.site!.internalSiteId,
      }
      : { customerName: '', internalName: '', internalSiteId: '' },
  });

  const create = useCreateSite(props.business?.id ?? '', {
    onSuccess: () => props.onSuccess(getValues()),
    onError: e => displayMessage.fail(e.message),
  });

  const update = useUpdateSite(props.site?.id ?? '', {
    onSuccess: () => props.onSuccess(getValues()),
    onError: e => displayMessage.fail(e.message),
  });

  const onSave = (values: UpsertSiteDto) => {
    const payload: UpsertSiteDto = {
      customerName: values.customerName.trim(),
      internalName: values.internalName.trim(),
      internalSiteId: values.internalSiteId.trim(),
    };
    if (isEdit) {
      update.request(payload);
    } else {
      create.request(payload);
    }
  };

  const hookProps = (name: Path<UpsertSiteDto>) => ({
    labelSpace: 4,
    inputSpace: 7,
    name,
    setValue,
    register,
    errors,
  });

  return <KaModal
    onHide={props.onHide}
    show={true}
    title={isEdit ? `Edit "${props.site!.customerName}"` : `Create a site for ${props.business!.name}`}
    body={(<>
      <KaAlert displayMessage={displayMessage.message} onClose={displayMessage.clear} />
      {atSiteAllowance && <Alert variant="info">
        This business has reached its site allowance of {props.business!.siteAllowance}. Increase the allowance via Manage sites before creating another.
      </Alert>}
      <RowFormText displayName="Kahler Site Name"
        {...hookProps('internalName')}
        options={{
          required: 'Kahler Site Name is required',
          validate: {
            length: v => v.length < 101 || 'Kahler Site Name cannot be longer than 100 characters',
          },
          pattern: {
            value: /^[^,]*$/i,
            message: 'Kahler Site Name cannot contain commas',
          },
        }}
      />
      <RowFormText displayName="Customer Site Name"
        {...hookProps('customerName')}
        options={{
          required: 'Customer Site Name is required',
          validate: {
            length: v => v.length < 101 || 'Name cannot be longer than 100 characters',
          },
          pattern: {
            value: /^[^,]*$/i,
            message: 'Name cannot contain commas',
          },
        }}
      />
      <RowFormText displayName="Site ID"
        {...hookProps('internalSiteId')}
        options={{
          required: 'Site ID is required',
          validate: {
            length: v => v.length < 101 || 'Site ID cannot be longer than 100 characters',
          },
        }}
      />
    </>)}
    footer={(
      <SpinnerButton
        className="btn-ghost-primary"
        spinning={isEdit ? update.isLoading : create.isLoading}
        disabled={atSiteAllowance}
        onClick={handleSubmit(onSave)}>
        {isEdit ? 'Save' : 'Create'}
      </SpinnerButton>
    )}
  />;
};

export default CreateEditSite;
